<template>
  <div class="hm-editor-page" v-rect-loading="loading">
    <div class="hm-editor-header">
      <div class="hm-editor-title">{{ titleLabel }}</div>
    </div>
    <div class="form-container">
      <WrappedFrom
        ref="form"
        class="hm-management-info"
        label-width="220px"
        :model="hmData"
        :formLines="formLines"
      >
        <template #companyId="{ lineName, line }">
          <custom-remote-el-select
            v-model="hmData[lineName]"
            v-bind="line.props"
            filterable
            :name="lineName"
            :placeholder="line.placeholder"
          >
            <el-option
              v-for="(option, index) in line.option.data"
              :key="index"
              :label="line.option.labelKey ? option[line.option.labelKey] : option.label"
              :value="line.option.valueKey ? option[line.option.valueKey] : option.value"
              :style="{ paddingLeft: `${option.level * 15}px` }"
            />
          </custom-remote-el-select>
        </template>
      </WrappedFrom>
      <div class="reset-password-btn">
        <a v-if="isEditMode && !isPasswordMode" @click="handleResetPassword">{{$t(`${i18nPrefix}.btn.resetPassword`)}}</a>
      </div>
      <div class="bottom-btn-group">
        <el-button class="cancel-btn" @click="handleCancel">{{ cancelBtnLabel }}</el-button>
        <el-button class="save-btn" @click="handleSave">{{ saveBtnLabel }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
// import _ from 'lodash'
import { selectLabelI18n, selectFuncLabelI18n, addI18nProperty, isEn } from '@/i18n'
import { mapActions } from 'vuex'
import { mixedRouterPush } from '@/router/routes'
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'
import { validateEmail } from '@/utils/validator'
import { passwordEncrypt } from '@/utils/rsa'

export default {
  components: {
    WrappedFrom
  },
  data () {
    return {
      i18nPrefix: 'admin.company.hmManagement',
      loading: false,
      loadFirst: true,
      hmData: {},
      isEn,
      isShowPermission: false,
      companyList: [],
      programTypeList: [],
      roleTypeList: {},
      reportToList: [],
      queryCompany: null,
      lastActiveMonth: null,
      selectLabelI18n,
      selectFuncLabelI18n
    }
  },
  computed: {
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    isPasswordMode () {
      return this.$route.meta.isPasswordMode
    },
    companyHmId () {
      return this.$route.params.companyHMId
    },
    titleLabel () {
      return this.$t(`${this.i18nPrefix}${this.isEditMode ? '.label.editHM' : '.label.addHM'}`)
    },
    cancelBtnLabel () {
      return selectLabelI18n(this, 'cancelBtn', 'common.btn.cancel')
    },
    saveBtnLabel () {
      return selectLabelI18n(this, 'saveBtn', 'common.btn.save')
    },
    formLines () {
      const lines = {
        first_name: {
          label: this.$t(`${this.i18nPrefix}.label.firstName`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.firstName`),
          rules: [{ required: true, message: this.$t(`${this.i18nPrefix}.message.hmRequired`), trigger: 'blur' }]
        },
        last_name: {
          label: this.$t(`${this.i18nPrefix}.label.lastName`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.lastName`),
          rules: [{ required: true, message: this.$t(`${this.i18nPrefix}.message.hmRequired`), trigger: 'blur' }]
        },
        company_id: {
          contentSlot: 'companyId',
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.company`),
          option: {
            data: this.companyList,
            valueKey: 'id',
            labelKey: `${isEn() ? 'name' : 'cn_name'}`
          },
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.company`)
        },
        email_address: {
          label: this.$t(`${this.i18nPrefix}.label.${this.isShowPermission ? 'primaryEmail' : 'email'}`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.${this.isShowPermission ? 'primaryEmail' : 'email'}`),
          disabled: this.isEditMode,
          rules: [
            { required: true, message: this.$t(`${this.i18nPrefix}.message.hmRequired`), trigger: 'blur' },
            { validator: validateEmail, trigger: 'blur' },
            { validator: this.validateEmailAddress, trigger: 'blur' }
          ]
        },
        password: {
          hidden: !this.isPasswordMode,
          label: this.$t(`${this.i18nPrefix}.label.password`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.password`),
          rules: [
            { required: true, message: this.$t(`${this.i18nPrefix}.message.hmRequired`), trigger: 'blur' },
            { validator: this.validatePasswordSame, trigger: 'blur' }
          ]
        },
        confirm_password: {
          hidden: !this.isPasswordMode,
          label: this.$t(`${this.i18nPrefix}.label.confirmPassword`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.confirmPassword`),
          rules: [
            { required: true, message: this.$t(`${this.i18nPrefix}.message.hmRequired`), trigger: 'blur' },
            { validator: this.validateConfirmPasswordSame, trigger: 'blur' }
          ]
        }
      }
      return lines
    },
    form () {
      const form = this.$refs.form
      return form
    }
  },
  created () {
    this.queryCompany = this.$route.query.company
    this.lastActiveMonth = this.$route.query.active_month
    this.initData()
  },
  methods: {
    ...mapActions('hm', ['getCompanyHm', 'saveCompanyHm', 'updateCompanyHm', 'resetCompanyHMPasswordByHR', 'hmEmailValidate']),
    ...mapActions('company', ['getCompanyChildrenTree']),
    async initData () {
      this.loading = true
      try {
        const companyTreeResp = await this.getCompanyChildrenTree()
        const currentCompany = {
          id: companyTreeResp.self.id,
          name: companyTreeResp.self.name,
          cn_name: companyTreeResp.self.cn_name,
          level: companyTreeResp.self.level
        }
        this.selectCompany = currentCompany.id
        const companyList = []
        this.companyList = this.formatCompanyData(companyTreeResp, currentCompany, companyList)
        if (this.isEditMode) {
          const resp = await this.getCompanyHm(this.companyHmId)
          this.hmData = {
            ...resp,
            company_id: resp.company.id
          }
        } else {
          this.hmData = {
            company_id: parseInt(this.queryCompany)
          }
        }
      } finally {
        this.loading = false
      }
    },
    async submit () {
      let resp
      if (this.isEditMode) {
        const data = { ...this.hmData }
        if (this.isPasswordMode) {
          data.password = passwordEncrypt(data.password)
          data.confirm_password = passwordEncrypt(data.confirm_password)
        }
        resp = await this.updateCompanyHm({ companyHmId: this.companyHmId, data: data })
      } else {
        resp = await this.saveCompanyHm(this.hmData)
      }
      if (resp) {
        this.$message.success(this.$t(`${this.i18nPrefix}.message.optionSuccess`))
        this.$router.replace({
          path: '/v-admin/hm/management/list',
          query: {
            company: this.queryCompany,
            order: '-updated_time',
            active_month: this.lastActiveMonth
          }
        })
      } else {
        this.$message.error(this.$t(`${this.i18nPrefix}.message.optionField`))
      }
    },
    async handleResetPassword () {
      if (this.loading) {
        return
      }
      this.loading = true
      try {
        const resp = await this.resetCompanyHMPasswordByHR(this.companyHmId)
        if (resp) {
          if (resp.send_limit) {
            this.$message.error(this.$t(`${this.i18nPrefix}.message.resetPasswordError`))
          } else {
            this.$message.success(this.$t(`${this.i18nPrefix}.message.resetPasswordSuccess`))
          }
        }
      } finally {
        this.loading = false
      }
    },
    handleCancel () {
      const backUrl = this.$route.query.back
      if (backUrl) {
        mixedRouterPush(this.$route.query.back)
      } else {
        this.$router.go(-1)
      }
    },
    async handleSave () {
      if (this.loading) {
        return
      }
      this.loading = true
      try {
        let result = false
        result = await this.form.submit()
        if (!result) {
          return
        }
        await this.submit()
      } finally {
        this.loading = false
      }
    },
    async validateEmailAddress (rule, value, callback) {
      if (this.isEditMode) {
        callback()
      }
      const params = {
        edit_company_hm_id: this.isEditMode ? this.companyHmId : null,
        company_id: this.hmData.company_id,
        email: value
      }
      const resp = await this.hmEmailValidate(params)
      if (resp) {
        if (resp.validate) {
          callback()
        } else {
          callback(new Error(this.$t(`${this.i18nPrefix}.message.emailExist`)))
        }
      }
    },
    validatePasswordSame (rule, value, callback) {
      if (this.validatePassword && this.hmData.password !== this.hmData.confirm_password) {
        callback(new Error(this.$t(`${this.i18nPrefix}.message.confirmPasswordSame`)))
      } else {
        callback()
      }
    },
    validateConfirmPasswordSame (rule, value, callback) {
      this.validatePassword = true
      this.validatePasswordSame(rule, value, callback)
    },
    formatCompanyData (data, company, companyList) {
      addI18nProperty(company, 'i18nName', 'cn_name', 'name')
      companyList.push(company)
      const allChildren = data.children
      const children = allChildren[company.id]
      if (children && children.length > 0) {
        for (const child of children) {
          this.formatCompanyData(data, child, companyList)
        }
      }
      return companyList
    }
  }
}
</script>

<style lang="scss" scoped>
.hm-editor-page {
  .hm-editor-header {
    display: flex;
    border-bottom: 1px solid #e4e7ed;

    .hm-editor-title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }

  .form-container {
    width: 606px;
    margin: 30px auto 0;
  }

  .reset-password-btn {
    margin-left: 220px;
    a {
      color: #0096cc;
      text-decoration: underline;
      cursor: pointer;
    }
  }

  .bottom-btn-group {
    display: flex;
    justify-content: flex-end;

    .el-button {
      padding: 8px 28px;
      border-radius: 2px;
      line-height: 22px;
      font-size: 16px;
      font-weight: 500;
      color: #0096cc;

      &:last-child {
        margin-left: 24px;
        margin-right: 0;
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }
    }
  }
}
</style>
